import { QRCodeSVG } from "qrcode.react";
import { Reveal } from "../components/landing/Reveal";

const APP_STORE_URL = "https://apps.apple.com/us/app/groomiq/id6787042178";

const PET_PARENT = {
  subhead: "Understand your dog's coat and keep up at home between spa days.",
  points: [
    ["Instant coat insights", "snap a photo and instantly see your dog's coat type, matting risk, and exactly what care it needs."],
    ["Know before you go", "show up to the groomer knowing the right questions to ask and what to expect."],
    ["At-home confidence", "simple, safe guidance for brushing, bathing, and drying between visits."],
    ["Safety first", "instant alerts for double-coats, sensitive breeds, and \u201Cdon't shave\u201D warnings so you never make a costly mistake."],
    ["Your dog's own profile", "save your pup, track grooming visits, and keep every note in one place."],
    ["Answers on demand", "get quick, trustworthy answers to your grooming questions anytime."],
  ],
  closing: "Give your pup a happy, healthy coat at home, and peace of mind that you're doing it right.",
};

const GROOMER = {
  subhead: "Faster consults. Safer decisions. Happier clients.",
  points: [
    ["Speed up every consult", "get an instant breed and coat read to guide your plan and set expectations in seconds."],
    ["Tool and technique guidance", "the right blades, methods, and drying approach for every coat."],
    ["Reduce risk and liability", "built-in safety flags for double-coats, matting, and senior dogs protect you and the pet."],
    ["Polished client handouts", "generate shareable groomer and client cards that make your service look premium."],
    ["An organized client book", "save every dog's profile, visit history, and notes so you're always prepared."],
    ["Look like the expert you are", "back every recommendation with clear, professional reasoning your clients trust."],
  ],
  closing: "Work smarter, protect every dog, and turn one-time visits into loyal regulars.",
};

const WHY = [
  ["Instant", "a simple at-home care plan for your pup from one photo, in seconds."],
  ["Made for pet parents", "no grooming experience needed, just a photo of your dog."],
  ["Safety-focused", "friendly guidance that always points you to your groomer or vet when it matters."],
  ["Private by design", "you control your data and can delete it anytime."],
];

function DownloadButton({ testid, dark = false }) {
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={dark
        ? "inline-flex items-center justify-center rounded-lg bg-white px-8 py-4 text-sm font-bold text-indigo-700 shadow-[0_10px_26px_-8px_rgba(0,0,0,0.4)] ring-1 ring-inset ring-white/60 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-8px_rgba(0,0,0,0.5)]"
        : "btn-indigo inline-flex items-center justify-center px-8 py-4 text-sm font-bold"}
    >
      <span>Download on the App Store</span>
    </a>
  );
}

function AudienceCard({ label, data, testid }) {
  return (
    <Reveal className="flex h-full flex-col rounded-lg border border-indigo-100 bg-white p-8 shadow-soft" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600">{label}</p>
      <p className="mt-4 font-serif text-2xl leading-snug text-stone-900">{data.subhead}</p>
      <ul className="mt-6 space-y-4">
        {data.points.map(([lead, rest]) => (
          <li key={lead} className="border-l-2 border-indigo-200 pl-4 text-sm leading-relaxed text-stone-600">
            <span className="font-semibold text-stone-900">{lead}:</span> {rest}
          </li>
        ))}
      </ul>
      <p className="mt-7 border-t border-indigo-50 pt-5 text-sm font-medium italic text-indigo-700">{data.closing}</p>
    </Reveal>
  );
}

function QrDownload({ testid, onDark = false }) {
  return (
    <div className="mt-8 hidden flex-col items-center gap-3 lg:flex" data-testid={testid}>
      <div className="rounded-xl bg-white p-3.5 shadow-soft ring-1 ring-black/5">
        <QRCodeSVG value={APP_STORE_URL} size={128} bgColor="#ffffff" fgColor="#3730a3" level="M" />
      </div>
      <p className={`text-xs font-medium uppercase tracking-widest ${onDark ? "text-indigo-100" : "text-stone-400"}`}>
        Scan to download on your phone
      </p>
    </div>
  );
}

export default function GroomIQPage() {  return (
    <>
      {/* Hero */}
      <section data-testid="groomiq-hero" className="bg-gradient-to-b from-indigo-50 to-white">
        <div className="mx-auto max-w-5xl px-5 py-16 text-center sm:px-8 lg:py-20">
          <Reveal>
            <img src="/assets/groomiq-logo.jpg" alt="GroomIQ" data-testid="groomiq-logo" className="mx-auto h-40 w-auto rounded-2xl sm:h-48" />
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mx-auto mt-8 max-w-3xl font-serif text-4xl leading-tight tracking-tight text-stone-900 sm:text-5xl lg:text-6xl" data-testid="groomiq-tagline">
              Keep your pup happy and healthy between spa days.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-stone-600 md:text-lg">
              Ever wonder what your dog's coat really needs at home? Snap one photo and GroomIQ shows
              you the likely breed, the coat type, how often to brush, the right brush to use, and the
              little things to watch for before they become problems. Simple, friendly tips that help
              you keep up at home until your pup's next spa day.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9">
              <DownloadButton testid="groomiq-appstore-button-hero" />
              <p className="mt-3 text-xs font-medium uppercase tracking-widest text-stone-400">Now available on the Apple App Store</p>
              <QrDownload testid="groomiq-qr-hero" />
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <a href="#groomiq-benefits" data-testid="groomiq-scroll-cue" className="mt-10 inline-block animate-bounce text-sm font-semibold uppercase tracking-[0.2em] text-indigo-500 transition-colors hover:text-indigo-700">
              Scroll down to see the benefits
            </a>
          </Reveal>
        </div>
      </section>

      {/* Audiences — lead with pet parents */}
      <section id="groomiq-benefits" data-testid="groomiq-audiences" className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-3xl space-y-8 px-5 sm:px-8">
          <AudienceCard label="For Pet Parents" data={PET_PARENT} testid="groomiq-pet-parent-card" />
          <AudienceCard label="For Professional Groomers" data={GROOMER} testid="groomiq-groomer-card" />
        </div>
      </section>

      {/* Why GroomIQ */}
      <section data-testid="groomiq-why" className="bg-indigo-50 py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600">Why GroomIQ</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-stone-900 sm:text-4xl">The smarter way to groom.</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {WHY.map(([lead, rest], i) => (
              <Reveal key={lead} delay={0.05 * i} className="rounded-lg bg-white p-6 shadow-soft">
                <p className="font-serif text-xl text-indigo-700">{lead}</p>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">{rest}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="groomiq-cta" className="bg-indigo-700 py-16 text-center lg:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl leading-snug tracking-tight text-white sm:text-4xl">
              The smarter way to groom starts here.
            </h2>
            <p className="mt-4 text-base text-indigo-100">Download GroomIQ today.</p>
            <div className="mt-8">
              <DownloadButton testid="groomiq-appstore-button-cta" dark />
              <QrDownload testid="groomiq-qr-cta" onDark />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
